import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/auth/AuthContext';
import SidePanel from './SidePanel';
import Footer from './Footer';
import Agents from '../../sections/dashboard/Agents';
import KnowledgeBase from '../../sections/dashboard/KnowledgeBase';
import PhoneNumbers from '../../sections/dashboard/PhoneNumbers';
// import VerifiedNumbers from '../../sections/dashboard/VerifiedNumbers'; // COMMENTED OUT
import BatchCall from '../../sections/dashboard/BatchCall';
import CallHistory from '../../sections/dashboard/CallHistory';
// import AssistantHistoryChat from '../../sections/dashboard/AssistantHistoryChat'; // COMMENTED OUT
import Analytics from '../../sections/dashboard/Analytics';
import Billing from '../../sections/dashboard/Billing';
// import BillingV1 from '../../sections/dashboard/billing-v1'; // COMMENTED OUT
// import Rewards from '../../sections/dashboard/rewards'; // COMMENTED OUT
import AdminDashboard from '../../sections/dashboard/AdminDashboard';
import PromoCode from '../../sections/dashboard/PromoCode';
import RetellAgentsDetails from '../../sections/dashboard/RetellAgentsDetails';
import DeletedRecords from '../../sections/dashboard/DeletedRecords';
import SignUpPeople from '../../sections/dashboard/SignUpPeople';
import Documentation from '../../sections/dashboard/Documentation';
import SetupBilling from '../../sections/dashboard/SetupBilling';

import Applications from '../../sections/dashboard/Applications';
import AgentDetails from '../../sections/dashboard/AgentDetails'; // Make sure this import is present
import AIAssistant from '../AIAssistant'; // COMMENTED OUT - Component disabled but import needed for code parsing

const DashboardLayout = ({ children, activePage, onPageChange, onOpenAssistant }) => {
  const { userName, userId, userEmail } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [applicationsPanelOpen, setApplicationsPanelOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [assistantPanelOpen, setAssistantPanelOpen] = useState(false);
  const [assistantContext, setAssistantContext] = useState(null);
  const [assistantPanelWidth, setAssistantPanelWidth] = useState(350); // Default width
  const [isResizing, setIsResizing] = useState(false);
  const [footerExpanded, setFooterExpanded] = useState(true); // true = footer visible to all users by default
  const footerRef = useRef(null);

  // Check if user is admin
  const isAdmin = userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au';

  // Handle assistant panel resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 220;
    const maxWidth = Math.min(800, window.innerWidth * 0.6); // Max 60% of screen width
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setAssistantPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      // Add visual feedback during resize
      document.body.style.opacity = '0.95';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.opacity = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.opacity = '';
    };
  }, [isResizing]);

  // Auto-reset panel width when assistant panel is opened
  // 🔒 FIX: Prevent page from shifting upward when assistant panel opens
  useEffect(() => {
    if (assistantPanelOpen) {
      // Save current scroll position before opening panel
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      // Set width based on screen size - wider on mobile for better usability
      const isMobileScreen = window.innerWidth <= 768;
      if (isMobileScreen) {
        // On mobile, use 90% of screen width for better visibility
        setAssistantPanelWidth(window.innerWidth * 0.9);
      } else {
        // On desktop, use default 350px
        setAssistantPanelWidth(350);
      }
      
      // Restore scroll position after a brief delay to prevent page shift
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
        // Also restore scroll position in the main content area
        const mainContent = document.querySelector('.app-body');
        if (mainContent) {
          const savedScrollTop = mainContent.scrollTop || 0;
          requestAnimationFrame(() => {
            mainContent.scrollTop = savedScrollTop;
          });
        }
      });
    }
  }, [assistantPanelOpen]);

  // Hide vertical scrollbar on dashboard mount
  useEffect(() => {
    document.body.classList.add('no-vertical-scroll');
    document.documentElement.classList.add('no-vertical-scroll');
    return () => {
      document.body.classList.remove('no-vertical-scroll');
      document.documentElement.classList.remove('no-vertical-scroll');
    };
  }, []);

  // 🔒 FIX: Prevent page scroll position from changing when assistant panel opens/closes
  useEffect(() => {
    if (assistantPanelOpen) {
      // Save current scroll positions
      const savedScrollY = window.scrollY || window.pageYOffset;
      const savedScrollX = window.scrollX || window.pageXOffset;
      const mainContent = document.querySelector('.app-body');
      const savedMainScrollTop = mainContent ? mainContent.scrollTop : 0;
      
      // Use a small delay to ensure layout has updated, then restore scroll position
      const restoreScroll = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Restore window scroll position
            if (window.scrollY !== savedScrollY || window.scrollX !== savedScrollX) {
              window.scrollTo(savedScrollX, savedScrollY);
            }
            // Restore main content scroll position
            if (mainContent && mainContent.scrollTop !== savedMainScrollTop) {
              mainContent.scrollTop = savedMainScrollTop;
            }
          });
        });
      };
      
      // Restore immediately and after a short delay
      restoreScroll();
      const timeoutId = setTimeout(restoreScroll, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [assistantPanelOpen]);

  // Check screen size for mobile responsiveness
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setApplicationsPanelOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Helper: check if children is AgentDetails
  const isAgentDetailsView =
    React.Children.count(children) === 1 &&
    children?.type?.name === 'AgentDetails';

  // Function to open AI Assistant with context
  const openAssistantWithContext = (context) => {
    setAssistantContext(context);
    setAssistantPanelOpen(true);
  };

  // Close footer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (footerExpanded && footerRef.current && !footerRef.current.contains(event.target)) {
        setFooterExpanded(false);
      }
    };

    if (footerExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [footerExpanded]);

  return (
    <>
      {/* Hide vertical scrollbar after login */}
      <style>{`
        * {
          box-sizing: border-box !important;
        }
        body, html {
          margin: 0 !important;
          padding: 0 !important;
          overflow-x: auto !important;
          overflow-y: hidden !important; /* Page doesn't scroll vertically on laptop */
          height: 100vh !important;
          max-height: 100vh !important;
          position: relative !important;
        }
        /* 🔒 FIX: Ensure no white space appears below footer - CRITICAL */
        body > *:last-child {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        /* Hide any content that might appear below viewport */
        body::after {
          display: none !important;
          content: none !important;
          height: 0 !important;
        }
        /* Ensure root element doesn't create space */
        #root {
          height: 100vh !important;
          max-height: 100vh !important;
          overflow: hidden !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        /* 🔒 FIX: On mobile, ensure root doesn't extend beyond footer */
        @media (max-width: 768px) {
          #root {
            height: auto !important;
            min-height: 100vh !important;
            max-height: none !important;
            overflow: visible !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          #root::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
        /* Remove all default padding and margins that create white gaps */
        .dashboard-layout {
          margin: 0 !important;
          padding: 0 !important;
          height: 100vh !important;
          max-height: 100vh !important;
          overflow: hidden !important;
        }
        .main-content-wrapper {
          margin: 0 !important;
          padding: 0 !important;
        }
        .no-vertical-scroll {
          overflow-y: hidden !important;
        }
        .dashboard-layout, .dashboard-layout * {
          scrollbar-width: none !important;
        }
        .dashboard-layout::-webkit-scrollbar, .dashboard-layout *::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          background: transparent !important;
        }
        /* 🔒 FIX: COMPLETELY REMOVE ALL WHITE SPACE BELOW FOOTER - AGGRESSIVE */
        .dashboard-footer-container {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          position: relative !important;
          height: auto !important;
          max-height: none !important;
        }
        .dashboard-footer-container .app-footer {
          padding-bottom: 0 !important;
          margin-bottom: 0 !important;
          margin: 0 !important;
          padding: 30px 0 0 0 !important;
        }
        .dashboard-footer-container > div {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
          margin: 0 !important;
        }
        .dashboard-footer-container > div > div {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        /* 🔒 FIX: Ensure footer is last element and nothing appears below it */
        .dashboard-footer-container::after,
        .dashboard-footer-container::before {
          display: none !important;
          content: none !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          line-height: 0 !important;
        }
        /* Hide any white space that might appear below footer */
        .dashboard-layout::after,
        .dashboard-layout::before {
          display: none !important;
          content: none !important;
          height: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
          line-height: 0 !important;
        }
        /* Remove white space from all possible sources */
        .dashboard-layout > *:last-child {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        /* Ensure no line breaks or whitespace create space */
        .dashboard-footer-container * {
          margin-bottom: 0 !important;
        }
        .dashboard-footer-container *:last-child {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
        }
        /* 🔒 FIX: Ensure close button is visible on mobile when assistant is open */
        @media (max-width: 768px) {
          /* Move assistant close button lower on mobile to ensure visibility */
          button[title="Close Assistant"] {
            position: fixed !important;
            top: 60px !important;
            right: 10px !important;
            z-index: 1002 !important;
          }
        }
        /* On mobile, allow page to scroll vertically and fix Android viewport issues */
        @media (max-width: 768px) {
          body, html {
            overflow-y: auto !important;
            height: auto !important;
            min-height: 100vh !important;
            min-height: -webkit-fill-available !important; /* Better mobile support for iOS/Android */
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Fix Android viewport height calculation */
          .dashboard-layout {
            min-height: 100vh !important;
            min-height: -webkit-fill-available !important;
            height: auto !important;
            overflow-y: auto !important; /* 🔒 FIX: Allow scrolling in landscape mode */
          }
          /* Ensure footer is always visible on Android */
          .dashboard-footer-container {
            position: relative !important;
            z-index: 100 !important;
            background: #000000 !important;
            width: 100% !important;
            flex-shrink: 0 !important;
            margin-top: auto !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Remove white box below footer - override Footer component padding */
          .dashboard-footer-container .app-footer {
            padding: 30px 0 0 0 !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .dashboard-footer-container > div {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* 🔒 FIX: Remove all bottom spacing from footer and its children on mobile */
          .dashboard-footer-container .app-footer {
            padding: 30px 0 0 0 !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
          }
          .dashboard-footer-container .app-footer * {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .dashboard-footer-container .app-footer .container {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .dashboard-footer-container .app-footer .footer-top {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Ensure no white space appears after footer */
          .dashboard-footer-container::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Remove any default browser spacing */
          body::after,
          html::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: Ensure footer container itself has no bottom spacing */
          .dashboard-footer-container {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: Remove any spacing from the last child of footer */
          .dashboard-footer-container .app-footer > *:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* 🔒 FIX: Remove spacing from minimized footer bar on mobile */
          .dashboard-footer-container > div {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
          }
          /* 🔒 FIX: Remove bottom border and spacing from minimized footer button */
          .dashboard-footer-container > div:first-child {
            border-bottom: none !important;
            margin-bottom: 0 !important;
            padding-bottom: 6px !important;
          }
          /* 🔒 FIX: Ensure no white space appears below footer on mobile */
          .dashboard-layout::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: Ensure body doesn't extend beyond footer on mobile */
          body {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* 🔒 FIX: Ensure dashboard layout ends exactly at footer on mobile */
          .dashboard-layout {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: Ensure main content wrapper doesn't create space below footer */
          .main-content-wrapper {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: COMPLETELY REMOVE ALL WHITE SPACE - MOBILE */
          .dashboard-footer-container {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .dashboard-footer-container * {
            margin-bottom: 0 !important;
          }
          .dashboard-footer-container *:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Remove any line breaks or whitespace */
          .dashboard-footer-container::after,
          .dashboard-footer-container::before {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 0 !important;
            font-size: 0 !important;
          }
          .dashboard-layout::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 0 !important;
            font-size: 0 !important;
          }
          /* Ensure body ends at footer */
          body {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          body::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Fix main content area for landscape scrolling */
          .app-body {
            overflow-y: auto !important;
            min-height: 0 !important;
            flex: 1 1 auto !important;
          }
        }
        /* Landscape orientation specific fixes */
        @media (max-width: 768px) and (orientation: landscape) {
          body, html {
            min-height: 100vh !important;
            min-height: -webkit-fill-available !important;
            height: auto !important;
            overflow-y: auto !important;
          }
          .dashboard-layout {
            min-height: 100vh !important;
            min-height: -webkit-fill-available !important;
            height: auto !important;
            overflow-y: auto !important; /* 🔒 FIX: Ensure scrolling works in landscape */
            display: flex !important;
            flex-direction: column !important;
          }
          /* Ensure main content area allows scrolling in landscape */
          .dashboard-layout > div:first-of-type {
            flex: 0 1 auto !important; /* 🔒 FIX: Don't grow to fill, allow footer space */
            min-height: 0 !important;
            max-height: calc(100vh - 60px) !important; /* 🔒 FIX: Reserve space for footer (60px) */
            overflow-y: auto !important;
            overflow-x: auto !important;
          }
          /* Use dynamic viewport height if supported */
          @supports (height: 100dvh) {
            .dashboard-layout > div:first-of-type {
              max-height: calc(100dvh - 60px) !important;
            }
          }
          /* Ensure footer is visible and at bottom in landscape */
          .dashboard-footer-container {
            position: relative !important;
            z-index: 100 !important;
            background: #000000 !important;
            width: 100% !important;
            flex-shrink: 0 !important;
            margin-top: auto !important; /* 🔒 FIX: Push footer to bottom in landscape */
            order: 999 !important; /* 🔒 FIX: Ensure footer is last element */
            display: block !important; /* 🔒 FIX: Ensure footer is displayed */
            visibility: visible !important; /* 🔒 FIX: Ensure footer is visible */
            opacity: 1 !important; /* 🔒 FIX: Ensure footer is not transparent */
          }
          /* Ensure main content wrapper doesn't take full height in landscape */
          .main-content-wrapper {
            max-height: calc(100vh - 60px) !important; /* 🔒 FIX: Reserve 60px for footer */
            overflow-y: auto !important;
            flex: 0 1 auto !important; /* 🔒 FIX: Don't grow to fill space */
            margin-bottom: 0 !important; /* 🔒 FIX: Remove bottom margin to eliminate white gap */
            padding-bottom: 0 !important; /* 🔒 FIX: Remove bottom padding to eliminate white gap */
          }
          /* Remove white gap between main content and footer */
          .dashboard-layout {
            gap: 0 !important; /* 🔒 FIX: Remove gap between flex items */
          }
          .main-content-wrapper + .dashboard-footer-container {
            margin-top: 0 !important; /* 🔒 FIX: Remove top margin from footer to eliminate gap */
          }
          /* 🔒 FIX: Remove all bottom spacing from footer container and its children in landscape */
          .dashboard-footer-container .app-footer {
            padding: 30px 0 0 0 !important;
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
            margin: 0 !important;
          }
          .dashboard-footer-container .app-footer * {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .dashboard-footer-container .app-footer .container {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          .dashboard-footer-container .app-footer .footer-top {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Ensure no white space appears after footer in landscape */
          .dashboard-footer-container::after {
            display: none !important;
            content: none !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* 🔒 FIX: Ensure footer container itself has no bottom spacing in landscape */
          .dashboard-footer-container {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* 🔒 FIX: Remove any spacing from the last child of footer in landscape */
          .dashboard-footer-container .app-footer > *:last-child {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* 🔒 FIX: Remove spacing from expanded footer wrapper div */
          .dashboard-footer-container > div {
            margin-bottom: 0 !important;
            padding-bottom: 0 !important;
          }
          /* Use dynamic viewport height if supported */
          @supports (height: 100dvh) {
            .main-content-wrapper {
              max-height: calc(100dvh - 60px) !important;
            }
          }
        }
        /* Use dynamic viewport height for better mobile support */
        @supports (height: 100dvh) {
          @media (max-width: 768px) {
            body, html {
              min-height: 100dvh !important;
            }
            .dashboard-layout {
              min-height: 100dvh !important;
            }
          }
          @media (max-width: 768px) and (orientation: landscape) {
            body, html {
              min-height: 100dvh !important;
            }
            .dashboard-layout {
              min-height: 100dvh !important;
            }
          }
        }
      `}</style>
      <div className="dashboard-layout" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: isMobile ? '100vh' : '100vh', 
        height: isMobile ? 'auto' : '100vh', 
        maxHeight: isMobile ? 'none' : '100vh',
        overflowY: isMobile ? 'auto' : 'hidden', 
        overflowX: 'auto', 
        width: '100vw', 
        position: 'relative', 
        margin: 0,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        padding: 0,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        gap: 0,
        boxSizing: 'border-box',
        lineHeight: 'normal'
      }}>
        {/* Main Content Area */}
        <div 
          className="main-content-wrapper"
          style={{ 
            display: 'flex', 
            flexDirection: 'row', 
            width: '100vw', 
            flex: isMobile ? '0 1 auto' : '1 1 auto', // 🔒 FIX: Don't grow in landscape mode to allow footer space
            background: '#f3f0ff', 
            minHeight: isMobile ? '0' : 0, // 🔒 FIX: Allow content to shrink in landscape
            overflowY: isMobile ? 'auto' : 'hidden', // 🔒 FIX: Allow scrolling in landscape mode
            overflowX: 'auto', 
            margin: 0, 
            marginBottom: 0, // 🔒 FIX: Remove bottom margin to eliminate white gap above footer
            padding: 0, 
            paddingBottom: 0, // 🔒 FIX: Remove bottom padding to eliminate white gap above footer
            height: isMobile ? 'auto' : '100%', // 🔒 FIX: Auto height in landscape to allow footer visibility
            alignItems: 'stretch', // 🔒 FIX: Stretch items to fill full height to eliminate bottom gap in assistant panel
            alignContent: 'flex-start', // 🔒 FIX: Align content to top
            maxHeight: isMobile ? 'calc(100vh - 60px)' : '100%', // 🔒 FIX: Reserve space for footer in landscape (60px for footer)
            position: 'relative' // 🔒 FIX: Relative positioning for proper child positioning
          }}
        >
          {/* SidePanel - 20% */}
          <div style={{ 
            width: sidebarOpen ? '250px' : '64px', // 🔒 FIX: Use fixed width instead of flexBasis to prevent changes when assistant panel opens
            flexBasis: sidebarOpen ? '250px' : '64px', // 🔒 FIX: Set flexBasis to match width for consistency
            minWidth: sidebarOpen ? 250 : 64, // 🔒 FIX: Match minWidth to width
            maxWidth: sidebarOpen ? 250 : 64, // 🔒 FIX: Match maxWidth to width to prevent expansion
            height: '100%', // 🔒 FIX: Use 100% to fill parent height properly
            minHeight: '100%', // 🔒 FIX: Ensure minimum height fills parent
            display: 'flex', // 🔒 FIX: Use flex to ensure SidePanel fills container
            flexDirection: 'column',
            transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
            alignSelf: 'stretch', // 🔒 FIX: Stretch to fill full height
            flexShrink: 0, // 🔒 FIX: Prevent SidePanel from shrinking when assistant panel opens
            flexGrow: 0 // 🔒 FIX: Prevent SidePanel from growing when assistant panel opens
          }}>
            <SidePanel
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              activePage={activePage}
              onPageChange={onPageChange}
              userName={userName}
              totalAmount={totalAmount}
            />
          </div>
          {/* Main Content - Dynamic width based on assistant panel */}
          <div style={{ 
            flexBasis: assistantPanelOpen ? `calc(100% - ${assistantPanelWidth}px - ${sidebarOpen ? '250px' : '64px'})` : (sidebarOpen ? 'calc(100% - 250px)' : 'calc(100% - 64px)'), // 🔒 FIX: Account for fixed SidePanel width when calculating main content width
            flexGrow: 1, 
            flexShrink: 1, 
            background: '#f3f0ff', 
            padding: '0', // 🔒 FIX: Remove all padding to eliminate white gaps
            paddingTop: '0', 
            paddingBottom: '0',
            paddingLeft: '0',
            paddingRight: '0',
            margin: '0', // 🔒 FIX: Remove all margins to eliminate white gaps
            marginTop: '0',
            marginBottom: '0',
            marginLeft: '0',
            marginRight: '0',
            minWidth: 0, 
            maxWidth: '100%', 
            minHeight: 0,
            height: '100%',
            transition: isResizing ? 'none' : 'all 0.3s cubic-bezier(.4,0,.2,1)', 
            overflowX: 'auto', 
            overflowY: 'hidden', /* Container doesn't scroll, only content inside */
            display: 'flex',
            flexDirection: 'column',
            // 🔒 FIX: Prevent vertical shift when assistant panel opens
            position: 'relative',
            willChange: 'auto' // Optimize for smooth transitions without causing shifts
          }}>
            {/* Top Right Assistant Button - COMMENTED OUT */}
            {false && (
              <div
                style={{
                  position: 'fixed',
                  top: isMobile && assistantPanelOpen ? '60px' : '10px',
                  right: '10px',
                  zIndex: 1002,
                  ...(isMobile && assistantPanelOpen ? {
                    top: '60px'
                  } : {})
                }}
                onMouseEnter={(e) => {
                  if (!assistantPanelOpen) {
                    const tooltip = e.currentTarget.querySelector('#dashboard-assistant-tooltip');
                    if (tooltip) tooltip.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  const tooltip = e.currentTarget.querySelector('#dashboard-assistant-tooltip');
                  if (tooltip) tooltip.style.opacity = '0';
                }}
              >
                <button
                  onClick={() => {
                    if (assistantPanelOpen) {
                      // 🔒 FIX: Close assistant without refreshing the page
                      setAssistantPanelOpen(false);
                      setAssistantContext(null);
                    } else {
                      // 🔒 FIX: Save scroll position before opening assistant panel
                      const scrollY = window.scrollY || window.pageYOffset;
                      const scrollX = window.scrollX || window.pageXOffset;
                      const mainContent = document.querySelector('.app-body');
                      const savedScrollTop = mainContent ? mainContent.scrollTop : 0;
                      
                      // Store scroll positions in sessionStorage to preserve across state changes
                      sessionStorage.setItem('assistantScrollY', scrollY.toString());
                      sessionStorage.setItem('assistantScrollX', scrollX.toString());
                      sessionStorage.setItem('assistantMainScrollTop', savedScrollTop.toString());
                      
                      // If opening the assistant, just toggle the state
                      setAssistantPanelOpen(true);
                      
                      // Restore scroll position after layout update
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          window.scrollTo(scrollX, scrollY);
                          if (mainContent) {
                            mainContent.scrollTop = savedScrollTop;
                          }
                        });
                      });
                    }
                  }}
                  style={{
                    background: assistantPanelOpen ? '#ef4444' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: assistantPanelOpen ? '50%' : '25px',
                    width: assistantPanelOpen ? '40px' : 'auto',
                    height: '40px',
                    fontSize: assistantPanelOpen ? '18px' : '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: assistantPanelOpen ? '0' : '8px 16px',
                    minWidth: assistantPanelOpen ? '40px' : '90px',
                    transition: 'all 0.3s ease'
                  }}
                  title={assistantPanelOpen ? "Close Assistant" : "Assistant"}
                >
                  {assistantPanelOpen ? '×' : 'Assistant'}
                </button>
                {/* Tooltip - Only show when assistant is closed */}
                {!assistantPanelOpen && (
                  <div
                    id="dashboard-assistant-tooltip"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '8px',
                      background: '#1f2937',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      opacity: 0,
                      pointerEvents: 'none',
                      transition: 'opacity 0.2s ease',
                      zIndex: 1001,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      right: '20px',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderBottom: '6px solid #1f2937'
                    }}></div>
                    For any issues let assistant help you
                  </div>
                )}
              </div>
            )}
            {/* Main Content */}
                         <main className={`app-body ${assistantPanelOpen ? 'assistant-open' : ''}`} style={{ 
                           flex: 1, 
                           background: 'transparent', // 🔒 FIX: Transparent background, no white
                           minHeight: 0, 
                           maxHeight: '100%', 
                           height: '100%', 
                           padding: '0', // 🔒 FIX: Remove all padding to eliminate white gaps
                           paddingTop: '0',
                           paddingBottom: '0',
                           paddingLeft: '0',
                           paddingRight: '0',
                           margin: '0', // 🔒 FIX: Remove all margins to eliminate white gaps
                           marginTop: '0',
                           marginBottom: '0',
                           marginLeft: '0',
                           marginRight: '0',
                           overflowY: 'auto', 
                           overflowX: 'auto', 
                           minWidth: '800px', 
                           display: 'flex', 
                           flexDirection: 'column', 
                           flexShrink: 1,
                           // 🔒 FIX: Prevent scroll position from changing when assistant panel opens
                           position: 'relative',
                           scrollBehavior: 'auto' // Use 'auto' instead of 'smooth' to prevent unwanted scrolling
                         }}>
               {activePage === 'Admin Dashboard' ? (
                 <AdminDashboard />
               ) : activePage === 'Sign Up People' ? (
                 <SignUpPeople />
               ) : activePage === 'Documentation' ? (
                 <Documentation />
               ) : activePage === 'Promo Code' ? (
                 <PromoCode />
               ) : activePage === 'Retell Agents Details' ? (
                 <RetellAgentsDetails />
               ) : activePage === 'Deleted Records' ? (
                 <DeletedRecords />
               ) : activePage === 'Setup Billing' ? (
                 <SetupBilling />
               ) : activePage === 'Home' ? (
                 <Agents onOpenAssistant={openAssistantWithContext} />
               ) : /* activePage === 'Awards & Winners' ? ( <Rewards /> ) : */ activePage === 'Knowledge Base' ? (
                 <KnowledgeBase />
               ) : activePage === 'Phone Numbers' ? (
                 <PhoneNumbers />
               ) : /* activePage === 'Verified Numbers' ? ( <VerifiedNumbers /> ) : */ activePage === 'Batch Call' ? (
                 <BatchCall />
               ) : activePage === 'Call History' ? (
                 <CallHistory />
               ) : /* activePage === 'Assistant Chat History' ? ( <AssistantHistoryChat /> ) : */ activePage === 'Analytics' ? (
                 <Analytics />
               ) : activePage === 'Billing' ? (
                 <Billing totalAmount={totalAmount} setTotalAmount={setTotalAmount} />
               ) : activePage === 'Billing V1' ? (
                 // <BillingV1 /> // COMMENTED OUT
                 <div
                   style={{
                     background: '#fff',
                     borderRadius: '50px',
                     boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                     padding: '20px',
                     minWidth: '900px',
                     maxWidth: '1100px',
                     width: '100%',
                     color: '#111',
                     marginTop: '0',
                     marginBottom: '0',
                     height: '700px',
                     display: 'flex',
                     flexDirection: 'column'
                   }}
                 >
                   <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>Billing V1</h2>
                 </div>
               ) : (
                 <div
                   style={{
                     background: '#fff',
                     borderRadius: '50px',
                     boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                     padding: '20px',
                     minWidth: '900px',
                     maxWidth: '1100px',
                     width: '100%',
                     color: '#111',
                    marginTop: '0',
                    marginBottom: '0',
                    height: '700px', // <-- Add this line
                     display: 'flex',
                     flexDirection: 'column'
                   }}
                 >
                   <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>{activePage}</h2>
                 </div>
               )}
             </main>
          </div>
          
          {/* Assistant Panel - Resizable - COMMENTED OUT */}
          {false && (
            <aside 
              className="assistant-panel" 
              style={{ 
                width: assistantPanelWidth,
                minWidth: isMobile ? window.innerWidth * 0.9 : 220, 
                maxWidth: isMobile ? window.innerWidth * 0.95 : Math.min(800, window.innerWidth * 0.6),
                background: '#ffffff', // 🔒 FIX: Use white background for better visibility
                borderLeft: '2px solid #e5e7eb', // 🔒 FIX: Thicker border for better visibility
                display: assistantPanelOpen ? 'flex' : 'none', 
                flexDirection: 'column', 
                alignItems: 'center', 
                height: '100%', // 🔒 FIX: Full height to eliminate bottom gap
                minHeight: '100%', // 🔒 FIX: Ensure minimum height is 100%
                position: isMobile ? 'fixed' : 'relative',
                right: isMobile ? '0' : 'auto',
                top: isMobile ? '0' : '0', // 🔒 FIX: Ensure panel starts from top
                bottom: isMobile ? '0' : '0', // 🔒 FIX: Ensure panel extends to bottom
                left: isMobile ? 'auto' : 'auto',
                zIndex: isMobile ? 1001 : 10, // 🔒 FIX: Higher z-index for better visibility
                padding: 0, 
                margin: 0,
                marginTop: 0, // 🔒 FIX: No top margin to prevent upward shift
                marginBottom: 0, // 🔒 FIX: No bottom margin to eliminate gap
                marginLeft: 0,
                marginRight: 0,
                paddingTop: 0, // 🔒 FIX: No top padding to prevent upward shift
                paddingBottom: 0, // 🔒 FIX: No bottom padding to eliminate gap
                paddingLeft: 0,
                paddingRight: 0,
                boxSizing: 'border-box', 
                transition: isResizing ? 'none' : 'all 0.3s cubic-bezier(.4,0,.2,1)',
                boxShadow: isMobile ? '0 0 20px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)', // 🔒 FIX: Add shadow for better visibility
                overflowY: 'auto',
                overflowX: 'hidden', // 🔒 FIX: Prevent horizontal overflow
                WebkitOverflowScrolling: 'touch',
                alignSelf: 'stretch', // 🔒 FIX: Stretch panel to fill full height to eliminate bottom gap
                // 🔒 FIX: Ensure panel content aligns to top and doesn't shift upward
                alignContent: 'flex-start',
                justifyContent: 'flex-start',
                verticalAlign: 'top',
                // 🔒 FIX: Ensure panel is clearly visible
                visibility: assistantPanelOpen ? 'visible' : 'hidden',
                opacity: assistantPanelOpen ? 1 : 0
              }}
            >
              {/* Resize Handle - Hidden on mobile */}
              {assistantPanelOpen && !isMobile && (
                <div
                  onMouseDown={handleMouseDown}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '4px',
                    height: '100%',
                    background: 'transparent',
                    cursor: 'col-resize',
                    zIndex: 10,
                    borderLeft: '2px solid transparent',
                    transition: 'border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderLeftColor = '#667eea';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderLeftColor = 'transparent';
                  }}
                  title="Drag to resize assistant panel"
                />
              )}
              
              {assistantPanelOpen && (
                <AIAssistant 
                  onClose={() => {
                    setAssistantPanelOpen(false);
                    setAssistantContext(null);
                  }}
                  isAgentDetails={false}
                  currentUser={{
                    id: userId,
                    email: userEmail,
                    full_name: userName
                  }}
                  formContext={assistantContext}
                />
              )}
            </aside>
          )}

        </div>
        
        {/* Footer - Minimized by default for dashboard */}
        <div 
          ref={footerRef} 
          className="dashboard-footer-container"
          style={{ 
            flexShrink: 0, 
            width: '100%', 
            margin: 0,
            marginTop: 0,
            marginBottom: 0,
            marginLeft: 0,
            marginRight: 0,
            padding: 0,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            position: isMobile ? 'relative' : 'relative',
            zIndex: isMobile ? 100 : 1,
            background: '#000000',
            minHeight: 'auto',
            maxHeight: 'none',
            height: 'auto',
            lineHeight: 'normal',
            fontSize: 'inherit'
          }}
        >
          {!footerExpanded ? (
            /* Minimized Footer Bar */
            <div 
              onClick={() => setFooterExpanded(true)}
              style={{
                width: '100%',
                padding: '6px 24px',
                paddingBottom: '6px',
                margin: 0,
                marginBottom: 0,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#000000',
                borderTop: '1px solid #333333',
                borderBottom: 'none',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{
                  color: '#ffffff',
                  fontSize: '12px'
                }}>
                  Click to expand
                </span>
                <i 
                  className="fas fa-chevron-up"
                  style={{
                    color: '#9ca3af',
                    fontSize: '12px',
                    transition: 'transform 0.3s ease'
                  }}
                ></i>
              </div>
            </div>
          ) : (
            /* Expanded Footer */
            <div style={{ 
              position: 'relative', 
              margin: 0, 
              padding: 0,
              background: '#000000'
            }}>
              <div 
                onClick={() => setFooterExpanded(false)}
                style={{
                  width: '100%',
                  padding: '6px 24px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: '#000000',
                  borderTop: '1px solid #333333',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#000000'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    color: '#ffffff',
                    fontSize: '12px'
                  }}>
                    Click to minimize
                  </span>
                  <i 
                    className="fas fa-chevron-down"
                    style={{
                      color: '#9ca3af',
                      fontSize: '12px',
                      transition: 'transform 0.3s ease'
                    }}
                  ></i>
                </div>
              </div>
              <Footer />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardLayout; 